/**
 * key-moments — «Ошибки и ключевые моменты»: единый список из
 * `speech.operatorErrors.errors` (ошибки) и `result.keyMoments` (ключевые
 * моменты) — как на экране. KeyMomentsSection.tsx:36-41 сливает оба
 * источника в один `timelineItems`, а не рисует их раздельными карточками;
 * ошибки идут первыми, моменты — следом, тот же порядок здесь.
 *
 * Заголовок карточки — `records.analysisPanel.errorsKeyMoments` («Errors &
 * Key Moments»), тот же ключ, что на экране. Согласованный прототип
 * (preview.html:310) рисует другой заголовок («Ключевые моменты») — это
 * расхождение прототипа с экраном, решено в пользу экрана.
 *
 * Цвет левой границы и бейдж — от ТИПА записи (ошибка/момент), а не от
 * `importance` ключевого момента, хотя прототип красит строки по важности
 * (`crit`/`high`/без класса). KeyMomentsSection.tsx явно (строки 62-67)
 * игнорирует `importance` для цвета — оно описывает заметность момента, а не
 * то, что оператор ошибся; красить по важности — тот самый баг, который там
 * уже однажды поправили («keying off importance here painted wins red and
 * tagged them 'Error' on calls the analysis found no fault with»).
 * Расхождение решено в пользу экрана: ошибки — `.moment crit` + бейдж
 * `errorTag` (bad), ключевые моменты — `.moment high` + бейдж `keyTag`
 * (warn), единообразно для всех уровней важности.
 *
 * Таймкод скрывается, если он выходит за длительность записи (+ допуск) —
 * тот же ответ, что на экране: `isTimecodeBeyondDuration` из `@aica/core`
 * (допуск отражает серверный клэмп
 * `apps/workflows-api/src/workers/analyze/safety-gates.ts`).
 * Момент при этом остаётся в списке — прячется только время, ровно как на
 * экране (комментарий H-5 там же): LLM иногда выдумывает время, которого не
 * было в записи, но сам текст момента обычно остаётся полезным наблюдением.
 *
 * Цитата печатается через «…», как в остальном отчёте (`wordListText` в
 * speech.ts, профанити там же), а не английские кавычки экрана
 * (`"…"` внутри склеенного `item.text` в KeyMomentsSection.tsx). Метка и
 * цитата — раздельные элементы разметки (`.tx`/`.q`), как в согласованном
 * прототипе, а не единая строка экрана — та же вёрсточная (не поведенческая)
 * правка, что уже сделана в dialogue.ts/recommendations.ts.
 */
import { card, tag } from '../primitives';
import { escapeHtml } from '../escape';
import { isTimecodeBeyondDuration, parseTimecode } from '@aica/core';
import type { SectionFn } from '../types';

interface TimelineItem {
  time?: string;
  label: string;
  quote?: string;
  borderClass: 'crit' | 'high';
  tagText: string;
  tagVariant: 'bad' | 'warn';
}

export const renderKeyMoments: SectionFn = (input, t) => {
  const { result } = input.analysis;
  const durationSeconds = input.record.durationSeconds;

  const items: TimelineItem[] = [];

  for (const err of result.speech?.operatorErrors?.errors ?? []) {
    items.push({
      label: err,
      borderClass: 'crit',
      tagText: t('records.analysisPanel.errorTag', 'Error'),
      tagVariant: 'bad',
    });
  }

  for (const m of result.keyMoments ?? []) {
    const speakerPrefix = m.speaker
      ? `${
          m.speaker === 'agent'
            ? t('records.analysisPanel.speakerShortAgent', '[Agent]')
            : m.speaker === 'customer'
              ? t('records.analysisPanel.speakerShortClient', '[Client]')
              : t('records.analysisPanel.speakerShortUnknown', '[Role unknown]')
        } `
      : '';
    // UI-side defence in depth (H-5, mirrored): a hallucinated timestamp
    // beyond the record's own duration hides just the timecode.
    const seconds = parseTimecode(m.timestamp);
    const outOfRange = isTimecodeBeyondDuration(seconds, durationSeconds);
    items.push({
      time: outOfRange ? undefined : (m.timestamp ?? undefined),
      label: `${speakerPrefix}${m.label}`,
      quote: m.quote || undefined,
      borderClass: 'high',
      tagText: t('records.analysisPanel.keyTag', 'Key'),
      tagVariant: 'warn',
    });
  }

  if (items.length === 0) return '';

  const rows = items
    .map((item) => {
      const timeHtml = item.time
        ? `<span class="tc">${escapeHtml(item.time)}</span>`
        : '';
      const quoteHtml = item.quote
        ? `<div class="q">«${escapeHtml(item.quote)}»</div>`
        : '';
      return (
        `<div class="moment ${item.borderClass}"><div class="m1">${timeHtml}` +
        `<span class="tx">${escapeHtml(item.label)}</span>${tag(item.tagText, item.tagVariant)}</div>` +
        `${quoteHtml}</div>`
      );
    })
    .join('');

  return card({
    title: t('records.analysisPanel.errorsKeyMoments', 'Errors & Key Moments'),
    value: `<span style="color:var(--fg3)">${items.length}</span>`,
    body: rows,
  });
};
